/*
 * THE LOCKBOX — the key, in 3D (sections/lockbox.liquid → [data-lockbox-key]).
 *
 * A Rajani MD key: polished steel like the vault on the hero photo, a round medallion with the
 * real Rx mark (assets/lockbox-key-rx.png, cut from the logo) on cream enamel inside a red lacquer
 * bezel, "RAJANI MD" around the rim, and a vault-style bit. It turns slowly and can be dragged;
 * a tap sends people to the email field — the whole point is "I want that key".
 *
 * Built from primitives + physically based materials lit by a small studio environment (softbox
 * panels rendered into a PMREM), so the steel picks up real reflections without an HDR download.
 *
 * Cost control: three.js (jsDelivr, ES module) is only fetched when the key is near the viewport;
 * rendering stops whenever it is off screen, hidden by state, or the tab is in the background.
 * No WebGL / the import fails → the block hides itself and the page is unchanged.
 *
 * Visibility is measured with getBoundingClientRect on load / scroll / resize, not with an
 * IntersectionObserver: on this page (iOS-sized viewport) the observer reported the block as
 * off screen once at load and never fired again, so the key never started.
 */
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.min.js';

const RED = 0xb5121b;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* On screen (with a margin) and not display:none — the block is hidden in the open/closed states. */
function nearViewport(el, margin = 0) {
  if (!el.offsetParent && getComputedStyle(el).position !== 'fixed') return false;
  const r = el.getBoundingClientRect();
  return r.bottom > -margin && r.top < window.innerHeight + margin && r.width > 0;
}

function webglAvailable() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) {
    return false;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/* Medallion face: cream enamel, the Rx mark, a thin red ring and RAJANI MD / THE LOCKBOX on the rim. */
async function medallionTexture(THREE, rxSrc, maxAnisotropy) {
  const size = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const c = size / 2;

  const bg = ctx.createRadialGradient(c * 0.8, c * 0.7, size * 0.05, c, c, c);
  bg.addColorStop(0, '#fffdfa');
  bg.addColorStop(0.7, '#f7f1eb');
  bg.addColorStop(1, '#e9dfd3');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = '#b5121b';
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.arc(c, c, size * 0.335, 0, Math.PI * 2);
  ctx.stroke();

  try {
    await document.fonts.load('700 56px Montserrat');
  } catch (e) {}
  ctx.fillStyle = '#1a1a1a';
  ctx.font = '700 56px Montserrat, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // letters placed by their measured width (plus tracking), so the arc reads evenly
  const arcText = (text, radius, centerAngle, inward) => {
    const tracking = 10;
    const widths = [...text].map((ch) => ctx.measureText(ch).width + tracking);
    const total = widths.reduce((a, b) => a + b, 0) - tracking;
    const dir = inward ? -1 : 1;
    let a = centerAngle - dir * (total / 2 / radius);
    [...text].forEach((ch, i) => {
      const half = (widths[i] - tracking) / 2 / radius;
      a += dir * half;
      ctx.save();
      ctx.translate(c + Math.cos(a) * radius, c + Math.sin(a) * radius);
      ctx.rotate(a + (inward ? -Math.PI / 2 : Math.PI / 2));
      ctx.fillText(ch, 0, 0);
      ctx.restore();
      a += dir * (half + tracking / radius);
    });
  };
  arcText('RAJANI MD', size * 0.415, -Math.PI / 2, false);
  arcText('THE LOCKBOX', size * 0.415, Math.PI / 2, true);
  [Math.PI, 0].forEach((a) => {
    ctx.beginPath();
    ctx.arc(c + Math.cos(a) * size * 0.415, c + Math.sin(a) * size * 0.415, 9, 0, Math.PI * 2);
    ctx.fillStyle = '#b5121b';
    ctx.fill();
  });

  try {
    const rx = await loadImage(rxSrc);
    const s = size * 0.5;
    ctx.drawImage(rx, c - s / 2 + size * 0.01, c - s / 2, s, s);
  } catch (e) {}

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = maxAnisotropy;
  return tex;
}

/* A soft contact shadow under the key. */
function shadowTexture(THREE) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(128, 32, 0, 128, 32, 128);
  g.addColorStop(0, 'rgba(0,0,0,0.55)');
  g.addColorStop(0.45, 'rgba(0,0,0,0.18)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 64);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/* Studio: a grey room with softboxes (bright planes) and one faint red panel, rendered into a PMREM. */
function studioEnvironment(THREE, renderer) {
  const env = new THREE.Scene();
  const room = new THREE.Mesh(
    new THREE.BoxGeometry(14, 9, 14),
    new THREE.MeshBasicMaterial({ color: 0x4a4a4c, side: THREE.BackSide })
  );
  env.add(room);
  const panel = (w, h, color, strength, pos, rot) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(strength), side: THREE.DoubleSide })
    );
    m.position.set(...pos);
    m.rotation.set(...rot);
    env.add(m);
  };
  panel(9, 3.2, 0xffffff, 6, [0, 4.4, 0], [Math.PI / 2, 0, 0]);            // overhead softbox
  panel(1.4, 6, 0xffffff, 5, [-6.9, 0.5, 1], [0, Math.PI / 2, 0]);          // left strip
  panel(1.8, 5, 0xfff4ea, 3.5, [6.9, 0.8, -1], [0, -Math.PI / 2, 0]);       // warm right strip
  panel(5, 2.2, 0xffffff, 1.6, [0, 0.6, 6.9], [0, Math.PI, 0]);             // front fill
  panel(3, 1.4, 0xff2a2a, 1.8, [-3, -2.6, -6.9], [0, 0, 0]);                // brand red kicker
  panel(14, 14, 0x1c1c1c, 1, [0, -4.45, 0], [-Math.PI / 2, 0, 0]);          // dark floor
  const pmrem = new THREE.PMREMGenerator(renderer);
  const tex = pmrem.fromScene(env, 0.03).texture;
  pmrem.dispose();
  return tex;
}

async function buildKey(THREE, renderer, rxSrc) {
  const steel = new THREE.MeshPhysicalMaterial({
    color: 0xe9e9ec,
    metalness: 1,
    roughness: 0.14,
    clearcoat: 0.3,
    clearcoatRoughness: 0.15
  });
  const satin = steel.clone();
  satin.roughness = 0.3;
  const lacquer = new THREE.MeshPhysicalMaterial({
    color: RED,
    metalness: 0.3,
    roughness: 0.25,
    clearcoat: 1,
    clearcoatRoughness: 0.06
  });
  const enamel = new THREE.MeshPhysicalMaterial({
    map: await medallionTexture(THREE, rxSrc, renderer.capabilities.getMaxAnisotropy()),
    metalness: 0,
    roughness: 0.45,
    clearcoat: 0.35,
    clearcoatRoughness: 0.12,
    envMapIntensity: 0.35   // keep the red Rx true red, not washed out by reflections
  });

  const key = new THREE.Group();

  // bow: bevelled steel medallion
  const R = 1.15;
  const disc = new THREE.Shape();
  disc.absarc(0, 0, R, 0, Math.PI * 2, false);
  const discGeo = new THREE.ExtrudeGeometry(disc, {
    depth: 0.14,
    bevelEnabled: true,
    bevelThickness: 0.06,
    bevelSize: 0.06,
    bevelSegments: 10,
    curveSegments: 128
  });
  discGeo.center();
  const bow = new THREE.Mesh(discGeo, steel);
  const bowX = -1.95;
  bow.position.x = bowX;
  key.add(bow);

  // enamel faces + red bezels, both sides
  [1, -1].forEach((side) => {
    const face = new THREE.Mesh(new THREE.CircleGeometry(0.9, 160), enamel);
    face.position.set(bowX, 0, side * 0.131);
    if (side < 0) face.rotation.y = Math.PI;
    key.add(face);
    const bezel = new THREE.Mesh(new THREE.TorusGeometry(0.93, 0.055, 32, 200), lacquer);
    bezel.position.set(bowX, 0, side * 0.128);
    key.add(bezel);
  });

  // key-ring loop on the far side of the bow
  const loop = new THREE.Mesh(new THREE.TorusGeometry(0.24, 0.07, 32, 96), steel);
  loop.position.set(bowX - R - 0.2, 0, 0);
  key.add(loop);

  // collar between bow and shaft
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.34, 96), satin);
  collar.rotation.z = Math.PI / 2;
  collar.position.x = bowX + R + 0.2;
  key.add(collar);
  [-0.17, 0.17].forEach((dx, i) => {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.045, 24, 96), i ? lacquer : steel);
    ring.rotation.y = Math.PI / 2;
    ring.position.x = collar.position.x + dx;
    key.add(ring);
  });

  // shaft
  const shaftStart = collar.position.x + 0.17;
  const shaftEnd = 2.25;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, shaftEnd - shaftStart, 96), steel);
  shaft.rotation.z = Math.PI / 2;
  shaft.position.x = (shaftStart + shaftEnd) / 2;
  key.add(shaft);
  const tip = new THREE.Mesh(new THREE.SphereGeometry(0.13, 48, 24), steel);
  tip.position.x = shaftEnd;
  key.add(tip);

  // vault-style bit with cuts
  const bit = new THREE.Shape();
  bit.moveTo(0, 0.06);
  bit.lineTo(0.78, 0.06);
  bit.lineTo(0.78, -1.0);
  bit.lineTo(0.6, -1.0);
  bit.lineTo(0.6, -0.72);
  bit.lineTo(0.44, -0.72);
  bit.lineTo(0.44, -1.0);
  bit.lineTo(0.24, -1.0);
  bit.lineTo(0.24, -0.58);
  bit.lineTo(0.0, -0.58);
  bit.lineTo(0, 0.06);
  const bitGeo = new THREE.ExtrudeGeometry(bit, {
    depth: 0.12,
    bevelEnabled: true,
    bevelThickness: 0.035,
    bevelSize: 0.035,
    bevelSegments: 6
  });
  bitGeo.translate(0, 0, -0.06);
  const bitMesh = new THREE.Mesh(bitGeo, steel);
  bitMesh.position.x = shaftEnd - 0.95;
  key.add(bitMesh);

  // centre the whole key on its own middle
  const box = new THREE.Box3().setFromObject(key);
  const centre = box.getCenter(new THREE.Vector3());
  key.children.forEach((m) => m.position.sub(centre));
  const size = box.getSize(new THREE.Vector3());
  return { key, size };
}

async function start(el) {
  const canvas = el.querySelector('canvas');
  if (!canvas || !webglAvailable()) {
    el.hidden = true;
    return;
  }

  let THREE;
  try {
    THREE = await import(THREE_URL);
  } catch (e) {
    el.hidden = true;
    return;
  }

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  scene.environment = studioEnvironment(THREE, renderer);

  const sun = new THREE.DirectionalLight(0xffffff, 1.6);
  sun.position.set(3, 5, 6);
  scene.add(sun);

  const camera = new THREE.PerspectiveCamera(26, 2, 0.1, 100);
  camera.position.set(0, 0.35, 12);
  camera.lookAt(0, 0, 0);

  const { key, size } = await buildKey(THREE, renderer, el.getAttribute('data-rx'));
  const rig = new THREE.Group();
  rig.add(key);
  rig.rotation.z = 0.16;
  scene.add(rig);

  const shadow = new THREE.Mesh(
    new THREE.PlaneGeometry(size.x * 1.05, 1.1),
    new THREE.MeshBasicMaterial({ map: shadowTexture(THREE), transparent: true, depthWrite: false })
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = -size.y * 0.62;
  if (!el.closest('.lockbox') || window.matchMedia('(min-width: 750px)').matches) scene.add(shadow);

  // fit the key's length to the canvas width (and its height to the canvas height)
  function resize() {
    const w = Math.max(1, el.clientWidth);
    const h = Math.max(1, el.clientHeight);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    const vFov = (camera.fov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
    const distW = (size.x * 0.56) / Math.tan(hFov / 2);
    const distH = (size.y * 0.92) / Math.tan(vFov / 2);
    camera.position.z = Math.max(distW, distH, 6);
    camera.updateProjectionMatrix();
    render();
  }

  // drag to spin, tap to go get it
  let spin = 0;
  let velocity = 0;
  let dragging = false;
  let lastX = 0;
  let moved = 0;
  canvas.addEventListener('pointerdown', (e) => {
    dragging = true;
    moved = 0;
    lastX = e.clientX;
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastX;
    lastX = e.clientX;
    moved += Math.abs(dx);
    spin += dx * 0.012;
    velocity = dx * 0.012;
    if (!running) render();
  });
  const release = () => {
    if (!dragging) return;
    dragging = false;
    if (moved < 6) {
      const section = el.closest('.lockbox');
      const state = section && section.getAttribute('data-state');
      const input = section && section.querySelector('.lockbox__panel--' + state + ' input[type="email"]');
      if (input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => input.focus({ preventScroll: true }), 400);
      }
    }
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);

  const clock = new THREE.Clock();
  let t = 0;
  function render() {
    // a slow sway (±55°) plus whatever the user dragged: the Rx medallion keeps facing out
    rig.rotation.y = spin + (reducedMotion ? 0.35 : Math.sin(t * 0.9) * 0.95);
    const bob = reducedMotion ? 0 : Math.sin(t * 1.3) * 0.09;
    rig.position.y = bob;
    rig.rotation.x = reducedMotion ? 0 : Math.sin(t * 0.8) * 0.07;
    shadow.material.opacity = 0.9 - bob * 2;
    shadow.scale.setScalar(1 - bob * 0.6);
    renderer.render(scene, camera);
  }

  let running = false;
  let frame = 0;
  function loop() {
    if (!running) return;
    const dt = Math.min(clock.getDelta(), 0.05);
    t += dt;
    if (!dragging) {
      velocity *= 0.94;
      spin += velocity;
      spin += (0 - spin) * 0.02; // drift back to facing the viewer after a drag
    }
    render();
    frame = requestAnimationFrame(loop);
  }
  function play() {
    if (running || reducedMotion) return;
    running = true;
    clock.getDelta();
    frame = requestAnimationFrame(loop);
  }
  function pause() {
    running = false;
    cancelAnimationFrame(frame);
  }

  new ResizeObserver(resize).observe(el);
  resize();
  spin = 0;
  render();
  el.classList.add('is-ready');

  const check = () => {
    if (!document.hidden && nearViewport(el)) play();
    else pause();
  };
  window.addEventListener('scroll', check, { passive: true });
  window.addEventListener('resize', check);
  document.addEventListener('visibilitychange', check);
  // state changes (unlock) hide the block without a scroll: re-check on a slow timer
  setInterval(check, 1000);
  check();
}

// Only fetch three.js once the key is about to be seen.
document.querySelectorAll('[data-lockbox-key]').forEach((el) => {
  if (el.__lockboxKey) return;
  el.__lockboxKey = true;
  const maybeStart = () => {
    if (el.__lockboxStarted || !nearViewport(el, 300)) return;
    el.__lockboxStarted = true;
    window.removeEventListener('scroll', maybeStart);
    window.removeEventListener('resize', maybeStart);
    start(el);
  };
  window.addEventListener('scroll', maybeStart, { passive: true });
  window.addEventListener('resize', maybeStart);
  if (document.readyState === 'complete') maybeStart();
  else window.addEventListener('load', maybeStart);
  setTimeout(maybeStart, 1500);
});
